
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import { useArtistsStore } from '@/store/ArtistsStore';
import { useAuthStore } from '@/store/AuthStore';
import { ArtistItem, GallerySystemArtist } from '@/dataTypes/types';
import ArtistCard from '@/components/artists/ArtistCard.vue';
import InsideSearch from '@/components/search/InsideSearch.vue';
import ArtistPopup from '@/components/Modal/ArtistPopup.vue';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import { useGalleryStore } from '@/store/GalleriesStore';
import { usePrivateSellersStore } from '@/store/PrivateSellersStore';
import PaginationComponent from '@/components/pagination/PaginationComponent.vue';
import { Container, Draggable } from 'vue3-smooth-dnd';
import Swal from 'sweetalert2';
import { DebouncedFunc, times } from 'lodash';
import _ from 'lodash';

export default defineComponent({
  components: {
    MainLayout,
    ArtistCard,
    InsideSearch,
    ArtistPopup,
    SpinnerLoader,
    PaginationComponent,
    Container,
    Draggable,
  },
  data() {
    const auth = useAuthStore();
    const artistsStore = useArtistsStore();
    const galleryStore = useGalleryStore();
    const privateSeller = usePrivateSellersStore();
    return {
      auth,
      artistsStore,
      galleryStore,
      privateSeller,
      isLoading: true,
      artists: [] as ArtistItem[],
      artistsLength: [] as ArtistItem[],
      allArtists: [] as GallerySystemArtist[],
      search: '',
      show: false,
      offset: 0,
      limit: 20,
      currentPage: 1,
      isSaving: false,
      debouncedSearch: null as DebouncedFunc<(keyword: string) => void> | null,
    };
  },
  async created() {
    if (this.auth.isLoggedIn) {
      await this.fetchData();
      this.isLoading = false;
    }
    this.debouncedSearch = _.debounce(async (keyword: string) => {
      await this.searchArtists(keyword);
    }, 500);
  },
  methods: {
    async fetchData(keyword?: string) {
      if (this.isPrivateSeller) {
        const [myArtists, allArtists] = await Promise.all([
          (this.currentArtistsListStore as any).fetchMyArtists(keyword),
          this.currentStore.getAllArtists({
            limit: 20,
            offset: 0,
          }),
        ]);
        this.artistsLength = myArtists;
        this.allArtists = allArtists;
      } else {
        const [artistsLength, allArtists] = await Promise.all([
          (this.currentArtistsListStore as any).fetchMyArtistsLength(keyword),
          this.currentStore.getAllArtists({
            limit: 20,
            offset: 0,
          }),
        ]);
        this.artistsLength = artistsLength;
        this.allArtists = allArtists;
      }

      this.artists = this.artistsLength?.slice(
        this.offset,
        this.offset + this.limit
      );

      this.offset = 0;
      this.currentPage = 1;
    },
    filterArtists(search: string) {
      this.search = search;
    },
    createIdOrderMap(artists: any[]) {
      const idOrderMap: any = {};
      const galleryArtists = artists.reverse();
      galleryArtists.forEach((item, index) => {
        const { id } = item;
        idOrderMap[id] = index + 1;
      });
      return idOrderMap;
    },
    async saveOrder() {
      const sortOrder = this.createIdOrderMap(this.artistsLength);
      this.isSaving = true;
      await this.currentStore.saveOrderOfArtists(sortOrder);
      this.isSaving = false;
    },
    toggleShow() {
      this.show = !this.show;
    },
    async handleArtistDelete(id: number) {
      let response;
      if (this.isPrivateSeller) {
        response = await this.privateSeller.removeArtistFromPrivateSeller(id);
      } else {
        response = await this.artistsStore.deleteArtist(id);
      }
      if (!response?.success) return;
      const index = this.filteredArtists.findIndex((item) => item.id === id);
      if (index !== -1) {
        this.filteredArtists.splice(index, 1);
      }
    },
    async updatePagination(pageNumber: number) {
      this.isLoading = true;
      this.offset = (pageNumber - 1) * this.limit;
      this.artists = this.artistsLength.slice(
        this.offset,
        this.offset + this.limit
      );
      this.currentPage = pageNumber;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.isLoading = false;
    },
    onDrop(dropResult: any) {
      let { removedIndex, addedIndex } = dropResult;
      if (this.currentPage > 1) {
        removedIndex = removedIndex + this.offset;
        addedIndex = addedIndex + this.offset;
      }
      if (removedIndex !== null && addedIndex !== null) {
        const movedItem = this.artistsLength.splice(removedIndex, 1)[0];
        this.artistsLength.splice(addedIndex, 0, movedItem);
        this.artists.splice(addedIndex, 0, movedItem);
        this.artistsStore.allGalleryArtists = this.artistsLength;
        const artistsIds = this.artistsLength.map((item) => item.id);
        this.artists = this.artistsLength.slice(
          this.offset,
          this.offset + this.limit
        );
        this.artistsStore.artistsOrder = artistsIds;
      }
    },
    moveUp(id: number) {
      const index = this.artistsLength.findIndex((item) => item.id === id);
      if (index !== -1) {
        if (index === 0 && this.offset === 0) {
          return;
        }
        const movedItem = this.artistsLength.splice(index, 1)[0];
        this.artistsLength.splice(index - 1, 0, movedItem);
        this.artistsStore.allGalleryArtists = this.artistsLength;
        const artistsIds = this.artistsLength.map((item) => item.id);
        this.artists = this.artistsLength.slice(
          this.offset,
          this.offset + this.limit
        );
        this.artistsStore.artistsOrder = artistsIds;
      }
    },
    moveDown(id: number) {
      const index = this.artistsLength.findIndex((item) => item.id === id);
      if (index !== -1 && index !== this.artistsLength.length - 1) {
        const movedItem = this.artistsLength.splice(index, 1)[0];
        this.artistsLength.splice(index + 1, 0, movedItem);
        this.artistsStore.allGalleryArtists = this.artistsLength;
        const artistsIds = this.artistsLength.map((item) => item.id);
        this.artists = this.artistsLength.slice(
          this.offset,
          this.offset + this.limit
        );
        this.artistsStore.artistsOrder = artistsIds;
      }
    },
    showAlert(id: number) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        confirmButtonColor: '#000',
        focusCancel: true,
      }).then((result) => {
        if (result.isConfirmed) {
          this.handleArtistDelete(id);
          Swal.fire('Deleted!', 'artist has been deleted.', 'success');
        }
      });
    },
    async addArtist(id: number) {
      let response;
      if (this.isPrivateSeller) {
        response = await this.privateSeller.addArtistToPrivateSeller(id);
      } else {
        response = await this.artistsStore.addArtistToGallery(id);
      }
      if (response?.success) {
        await this.fetchData();
        Swal.fire({
          title: 'Success!',
          text: 'Artist added to CMS',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#000',
        });
        this.show = false;
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'Something went wrong',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#000',
        });
      }
    },
    async searchArtists(keyword: string) {
      await this.fetchData(keyword);
    },
  },
  computed: {
    isPrivateSeller() {
      return this.auth.userRole === 'private_seller';
    },
    currentStore() {
      return this.isPrivateSeller ? this.privateSeller : this.galleryStore;
    },
    currentArtistsListStore() {
      return this.isPrivateSeller ? this.privateSeller : this.artistsStore;
    },
    filteredArtists(): ArtistItem[] {
      return this.artists;
    },
    pageSize(): number {
      if (this.artistsLength.length < 20) {
        return this.artistsLength.length;
      } else {
        return 20;
      }
    },
    length(): number {
      return this.artistsLength.length;
    },
  },
  watch: {
    async search(newValue: string, oldValue: string) {
      if (this.debouncedSearch) {
        this.debouncedSearch(newValue);
      }
    },
  },
});
